import { useCallback, useEffect, useState } from "react";
import { useSite } from "../context/siteContext";
import { getCasesBySite, getWorkordersBySite } from "../services/servicesService";
import { mapCasesResponse } from "../utilities/serviceMappings";

const useAsyncData = (deps, fetcher) => {
	const [data, setData] = useState([]);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(null);

	const run = useCallback(async () => {
		if (!deps.every(Boolean)) {
			setData([]);
			return;
		}
		setLoading(true);
		setError(null);
		try {
			const response = await fetcher();
			setData(response ?? []);
		} catch (err) {
			console.error("useAsyncData error:", err);
			setError(err);
		} finally {
			setLoading(false);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, deps);

	useEffect(() => {
		run();
	}, [run]);

	return { data, loading, error, refresh: run };
};

export const useCasesData = () => {
	const { selectedSite } = useSite();

	const fetcher = useCallback(async () => {
		const response = await getCasesBySite(selectedSite);
		if (!response?.status) return [];
		return mapCasesResponse(response.data || []);
	}, [selectedSite]);

	const { data, loading, error, refresh } = useAsyncData([selectedSite], fetcher);
	return { cases: data, casesLoading: loading, casesError: error, refreshCases: refresh };
};

export const useWorkordersData = () => {
	const { selectedSite } = useSite();

	const fetcher = useCallback(async () => {
		const response = await getWorkordersBySite(selectedSite);
		if (!response?.status) return [];
		return response.data || [];
	}, [selectedSite]);

	const { data, loading, error, refresh } = useAsyncData([selectedSite], fetcher);
	return { workorders: data, workordersLoading: loading, workordersError: error, refreshWorkorders: refresh };
};
